'use client';

import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { MOCK_FEED_CONSUMPTION, FeedConsumption } from './feedData';

export default function FeedConsumptionTab() {
  const [records] = useState<FeedConsumption[]>(MOCK_FEED_CONSUMPTION);
  const [search, setSearch] = useState('');
  const [filterFarm, setFilterFarm] = useState('all');

  const farms = Array.from(new Set(records.map(r => r.farm)));

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.flockName.toLowerCase().includes(q) || r.feedTypeName.toLowerCase().includes(q) || r.house.toLowerCase().includes(q);
    const matchFarm = filterFarm === 'all' || r.farm === filterFarm;
    return matchSearch && matchFarm;
  });

  const totalConsumed = filtered.reduce((s, r) => s + r.consumedKg, 0);
  const totalWastage = filtered.reduce((s, r) => s + r.wastageKg, 0);
  const wastageRate = totalConsumed > 0 ? ((totalWastage / (totalConsumed + totalWastage)) * 100).toFixed(1) : '0.0';

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Consumed (kg)', value: totalConsumed.toLocaleString(), color: 'text-indigo-600' },
          { label: 'Total Wastage (kg)', value: totalWastage.toFixed(1), color: 'text-amber-600' },
          { label: 'Wastage Rate', value: `${wastageRate}%`, color: totalWastage / totalConsumed > 0.05 ? 'text-red-600' : 'text-emerald-600' },
          { label: 'Flocks Tracked', value: new Set(filtered.map(r => r.flockId)).size, color: 'text-purple-600' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search consumption records…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Farms</option>
          {farms.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Date','Flock','House / Farm','Feed Type','Week','Opening (kg)','Issued (kg)','Returned (kg)','Consumed (kg)','Wastage (kg)','Closing (kg)','Birds','g/Bird/Day'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const wastageRate = r.consumedKg > 0 ? (r.wastageKg / (r.consumedKg + r.wastageKg)) * 100 : 0;
              return (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{r.flockName}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    <div className="text-xs">{r.house}</div>
                    <div className="text-xs text-gray-400">{r.farm}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-700">{r.feedTypeName}</div>
                    <div className="text-xs text-gray-400">{r.feedCode}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">Wk {r.weekNumber}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{r.openingStockKg}</td>
                  <td className="px-4 py-3 text-right text-indigo-600 font-medium">{r.issuedKg}</td>
                  <td className="px-4 py-3 text-right text-purple-600">{r.returnedKg > 0 ? r.returnedKg : '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-600">{r.consumedKg.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${wastageRate > 5 ? 'text-red-600' : wastageRate > 2 ? 'text-amber-600' : 'text-gray-500'}`}>{r.wastageKg.toFixed(1)}</span>
                    <div className="text-xs text-gray-400">{wastageRate.toFixed(1)}%</div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500">{r.closingStockKg.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{r.birdCount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-700">{r.avgConsumptionPerBirdGrams.toFixed(1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No consumption records found</div>}
      </div>
    </div>
  );
}
